export const SHOPIFY_BACKFILL_TYPES = ['products', 'orders', 'checkouts'] as const;

export type ShopifyBackfillType = (typeof SHOPIFY_BACKFILL_TYPES)[number];

export interface ShopifyBackfillRun {
  startedAt: number;
  pending: readonly ShopifyBackfillType[];
}

const isBackfillType = (value: unknown): value is ShopifyBackfillType =>
  typeof value === 'string' &&
  (SHOPIFY_BACKFILL_TYPES as readonly string[]).includes(value);

const runs = new Map<string, ShopifyBackfillRun>();
const listeners = new Set<() => void>();

const emit = () => {
  for (const listener of listeners) listener();
};

export function subscribeShopifyBackfill(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getShopifyBackfill(
  scopeId: string | null | undefined,
): ShopifyBackfillRun | null {
  if (!scopeId) return null;
  return runs.get(scopeId) ?? null;
}

export function startShopifyBackfill(
  scopeId: string | null | undefined,
  queued: readonly unknown[] | undefined,
): void {
  if (!scopeId) return;
  const pending = (queued ?? []).filter(isBackfillType);
  if (pending.length === 0) {
    if (runs.delete(scopeId)) emit();
    return;
  }
  runs.set(scopeId, { startedAt: Date.now(), pending });
  emit();
}

export function completeShopifyBackfill(
  scopeId: string | null | undefined,
  type: unknown,
): void {
  if (!scopeId || !isBackfillType(type)) return;
  const run = runs.get(scopeId);
  if (!run || !run.pending.includes(type)) return;
  const pending = run.pending.filter((t) => t !== type);
  if (pending.length === 0) runs.delete(scopeId);
  else runs.set(scopeId, { startedAt: run.startedAt, pending });
  emit();
}

export function clearShopifyBackfill(scopeId: string | null | undefined): void {
  if (!scopeId) return;
  if (runs.delete(scopeId)) emit();
}

export function resetShopifyBackfills(): void {
  runs.clear();
  emit();
}
